import type { StaleOpenPeriod } from '@repo/shared-types';

/**
 * The month whose close settles the oldest stranded period in `staleOpen`, or
 * `null` when nothing is stale. Computed rather than read off `staleOpen[0]`
 * so a change in the server's ordering cannot silently retarget what a
 * consumer tells the teacher to do.
 *
 * Shared by the chốt sổ nudge (`chotNudge.ts`) and the close-month screen
 * (`CloseMonthPage.tsx`) — both dashboard presentation, so this lives here
 * rather than in `chotNudge.ts` (named and documented for the nudge alone) or
 * in `@repo/shared-types` (nothing here crosses the network or the DB).
 */
export function oldestOwedMonth(staleOpen: StaleOpenPeriod[]): string | null {
  return staleOpen.length > 0
    ? staleOpen.reduce((min, s) => (s.closeLabel < min ? s.closeLabel : min), staleOpen[0]!.closeLabel)
    : null;
}

/** One guidance row on the close-month screen: everything about ONE month that
 *  is still owed, already grouped, deduped and capped for display. */
export interface StaleOpenMonthRow {
  /** 'YYYY-MM' — the month to close. Also the grouping key and the target of
   *  the row's "Chuyển sang tháng {{m}}" action: `closeLabel` means "the month
   *  to close" under BOTH reasons (see StaleOpenPeriod), which is why grouping
   *  on it needs no branch on `reason`. */
  closeLabel: string;
  /** Distinct student names on this month, in first-seen order, capped at
   *  `nameLimit`. Names — not enrollments: a student with two stranded
   *  subjects is one person to the teacher reading the row. */
  namesShown: string[];
  /** Distinct students beyond `namesShown`; 0 when they all fit. */
  namesExtra: number;
  /** Every original row for this month, order preserved, for the row's
   *  "Xem chi tiết" fold (which still renders the per-enrollment copy). */
  entries: StaleOpenPeriod[];
}

export interface StaleOpenRowsResult {
  /** Oldest `closeLabel` first, capped at `monthLimit`. */
  rows: StaleOpenMonthRow[];
  /** Months dropped by `monthLimit`; 0 when they all fit. */
  hiddenMonths: number;
}

/**
 * Fold `preview.staleOpen` — one row per enrollment — into at most `monthLimit`
 * guidance rows, one per month owed, oldest first (design §2).
 *
 * Why grouping is the whole job: the dominant real trigger is an org-wide
 * skipped month, so the raw array is N enrollments of the SAME month. Rendered
 * one-per-enrollment (as it was before P2.1) it became a wall of text that
 * pushed the payer list — the actual work of the screen — off-screen, while
 * saying one thing N times. Grouped, it says one thing once, with the names
 * behind it.
 *
 * 'YYYY-MM' sorts lexicographically in calendar order, so a plain `.sort()` on
 * the keys is the chronological sort — no Date parsing, no timezone.
 *
 * Pure: no i18n, no joining. The caller owns "A, B và 2 người khác" because
 * that sentence is copy, and copy is translated.
 */
export function staleOpenRows(
  staleOpen: StaleOpenPeriod[],
  { monthLimit = 3, nameLimit = 2 }: { monthLimit?: number; nameLimit?: number } = {},
): StaleOpenRowsResult {
  const byMonth = new Map<string, StaleOpenPeriod[]>();
  for (const period of staleOpen) {
    const bucket = byMonth.get(period.closeLabel);
    if (bucket) bucket.push(period);
    else byMonth.set(period.closeLabel, [period]);
  }

  const months = [...byMonth.keys()].sort();
  const rows = months.slice(0, monthLimit).map((closeLabel) => {
    const entries = byMonth.get(closeLabel)!;
    const names: string[] = [];
    for (const entry of entries) {
      if (!names.includes(entry.studentName)) names.push(entry.studentName);
    }
    return {
      closeLabel,
      namesShown: names.slice(0, nameLimit),
      namesExtra: Math.max(0, names.length - nameLimit),
      entries,
    };
  });

  return { rows, hiddenMonths: Math.max(0, months.length - monthLimit) };
}
